import React from 'react';
import './style.css';
import AuraButtonGreen from '../AuraButton/AuraButtonGreen';
import { Link } from 'react-router-dom';

const AuraArchitectureSection = () => {
    const workflowCards = [
        {
            icon: 'fa-solid fa-user',
            title: 'Patient Portal',
            description: 'QR-based onboarding, appointment booking, and real-time check-in/check-out for seamless patient journeys.',
            color: '#22c55e',
            badgeText: 'Flow 1'
        },
        {
            icon: 'fa-solid fa-stethoscope',
            title: 'Practitioner Portal',
            description: 'Access schedules, patient records, AI-assisted summaries, and clinical note management in one place.',
            color: '#3b82f6',
            badgeText: 'Flow 2'
        },
        {
            icon: 'fa-solid fa-building',
            title: 'Clinic Portal',
            description: 'Manage QR codes, approvals, services, MBS reviews, and practitioner assignments with full BP integration.',
            color: '#a855f7',
            badgeText: 'Flow 3'
        }
    ];

    return (
        <section className="aura-architecture-section">
            <div className="container">
                {/* Section Header */}
                <div className="row">
                    <div className="col-lg-8 mx-auto text-center">
                        {/* <span className="aura-architecture-badge">
                            <i className="fa-solid fa-sitemap me-2"></i>
                            Platform Architecture
                        </span> */}
                        <h2 className="aura-architecture-heading">
                            Unified Workflow Architecture for Modern Clinics
                        </h2>
                        <p className="description-text mb-5">
                            Aura Clinic connects patients, practitioners, and clinic staff through intelligent, secure workflows that integrate seamlessly with Best Practice (BP) APIs.
                        </p>
                    </div>
                </div>

                {/* Workflow Cards */}
                <div className="row g-4 mb-5">
                    {workflowCards.map((card, index) => (
                        <div className="col-lg-4 col-md-6" key={index}>
                            <div className="aura-workflow-card">
                                <div className="aura-workflow-card-header">
                                    <div className="aura-workflow-icon" style={{ backgroundColor: card.color }}>
                                        <i className={card.icon}></i>
                                    </div>
                                    <span className="aura-workflow-badge" style={{ backgroundColor: `${card.color}20`, color: card.color, borderColor: card.color }}>
                                        {card.badgeText}
                                    </span>
                                </div>
                                <h3 className="aura-workflow-card-title">{card.title}</h3>
                                <p className="aura-workflow-card-description">{card.description}</p>
                                <div className="aura-workflow-features">
                                    <div className="aura-workflow-feature">
                                        <i className="fa-solid fa-database"></i>
                                        <span>BP API Integration</span>
                                    </div>
                                    <div className="aura-workflow-feature">
                                        <i className="fa-solid fa-shield-halved"></i>
                                        <span>Secure Data Flow</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Architecture Diagram Preview */}
                <div className="aura-architecture-preview">
                    <div className="row align-items-center">
                        <div className="col-lg-6">
                            <div className="aura-architecture-content">
                                <h3 className="aura-architecture-subtitle">
                                    Comprehensive Workflow Documentation
                                </h3>
                                <p className="description-text">
                                    Explore detailed step-by-step workflows showing how each portal interacts with BP APIs. Understand the complete data flow from patient onboarding to clinical documentation.
                                </p>
                                <ul className="aura-architecture-list">
                                    <li>
                                        <i className="fa-solid fa-circle-check"></i>
                                        <span>Patient onboarding & appointment flow</span>
                                    </li>
                                    <li>
                                        <i className="fa-solid fa-circle-check"></i>
                                        <span>Practitioner schedule & AI assistant integration</span>
                                    </li>
                                    <li>
                                        <i className="fa-solid fa-circle-check"></i>
                                        <span>Clinic management & MBS compliance workflows</span>
                                    </li>
                                    <li>
                                        <i className="fa-solid fa-circle-check"></i>
                                        <span>Complete BP API interaction mapping</span>
                                    </li>
                                </ul>
                                <AuraButtonGreen text='View Full Workflow Documentation' link='/solutions/auraclinic/flow' />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="aura-architecture-diagram">
                                <div className="aura-diagram-card">
                                    <div className="aura-diagram-header">
                                        <span className="aura-diagram-dot red"></span>
                                        <span className="aura-diagram-dot yellow"></span>
                                        <span className="aura-diagram-dot green"></span>
                                        <span className="aura-diagram-title">Platform Architecture</span>
                                    </div>
                                    <div className="aura-diagram-body">
                                        {/* Simplified Architecture Visualization */}
                                        <div className="aura-diagram-flow">
                                            <div className="aura-diagram-node aura-diagram-patient">
                                                <i className="fa-solid fa-user"></i>
                                                <span>Patient</span>
                                            </div>
                                            <div className="aura-diagram-connector">
                                                <div className="aura-connector-line"></div>
                                                <div className="aura-connector-arrow"></div>
                                            </div>
                                            <div className="aura-diagram-node aura-diagram-aura">
                                                <i className="fa-solid fa-hospital"></i>
                                                <span>Aura Clinic</span>
                                            </div>
                                            <div className="aura-diagram-connector">
                                                <div className="aura-connector-line"></div>
                                                <div className="aura-connector-arrow"></div>
                                            </div>
                                            <div className="aura-diagram-node aura-diagram-bp">
                                                <i className="fa-solid fa-database"></i>
                                                <span>BP APIs</span>
                                            </div>
                                        </div>
                                        <div className="aura-diagram-portals">
                                            <div className="aura-portal-item">
                                                <span className="aura-portal-badge patient">P</span>
                                                <span>Patient Portal</span>
                                            </div>
                                            <div className="aura-portal-item">
                                                <span className="aura-portal-badge practitioner">D</span>
                                                <span>Practitioner</span>
                                            </div>
                                            <div className="aura-portal-item">
                                                <span className="aura-portal-badge clinic">C</span>
                                                <span>Clinic Portal</span>
                                            </div>
                                        </div>
                                        <div className="aura-diagram-api-box">
                                            <div className="aura-api-label">
                                                <i className="fa-solid fa-arrows-rotate"></i>
                                                <span>READ/WRITE Operations</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AuraArchitectureSection;
